import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Stack,
  Tooltip,
} from "@altimateai/lego";
import warning from "../../assets/icons/warning.svg";
import styles from "./styles.module.scss";
import { getScopeString } from "../../helpers/utils";
import classnames from "classnames";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getIncidentById, getDebugSQLByIncidentId, postSnoozeIncident } from "../../helpers/apis";
import { formatDateV2, isSnoozedFor1Day } from "./helper";
import { useEffect, useState } from "react";
import { ComponentLoader, Loader } from "../../components/loader";
import { ROCodeEditor, CueCodeEditor } from "@/components/codeEditor";
import { CopyIconButtonFab } from "../../components/copyButton";
import { DangerCircleIcon } from "@/assets/icons";
import { GET_INCIDENTS } from "../../services/filters";

const GET_INCIDENT_ID = "GET-INCIDENT-ID";
const GET_DEBUG_SQL = "GET-DEBUG-SQL";

interface ViolationItem {
  id: number;
  violated_on: string;
  actual: string | null;
  [key: string]: unknown;
}

function isViolationItem(item: unknown): item is ViolationItem {
  return typeof item === "object" && item !== null && "id" in item && "violated_on" in item;
}

interface IncidentModalProps {
  incidentId: string;
  isOpen: boolean;
  recentIncidentIds: Record<string, boolean>;
  toggle: () => void;
  onIncidentClick: (id: number) => void;
}

const IncidentModal = ({
  incidentId,
  isOpen,
  recentIncidentIds,
  toggle,
  onIncidentClick,
}: IncidentModalProps) => {
  const { data, isLoading } = useQuery({
    queryKey: [GET_INCIDENT_ID, incidentId],
    queryFn: () => getIncidentById(incidentId),
  });
  const { data: debugSQLData, isLoading: isDebugSQLLoading } = useQuery({
    queryKey: [GET_DEBUG_SQL, incidentId],
    queryFn: () => getDebugSQLByIncidentId(incidentId),
  });
  const [sortedIncidents, setSortedIncidents] = useState<ViolationItem[]>([]);
  const [showDebugData, setShowDebugData] = useState(false);

  useEffect(() => {
    if (!data) return;
    setSortedIncidents((data.last_violations_for_term ?? []).filter(isViolationItem));
  }, [data]);

  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: postSnoozeIncident,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_INCIDENT_ID, incidentId],
      });
      queryClient.invalidateQueries({ queryKey: [GET_INCIDENTS] });
    },
  });

  if (isLoading) {
    return <Loader />;
  }

  const toggleDebugSQL = () => {
    setShowDebugData(!showDebugData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && toggle()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="fs-2">Incident: #{incidentId}</DialogTitle>
        </DialogHeader>
        <div className={styles.incident_modal}>
          <Stack align="center" gap="sm" className="mb-3">
            <div className={styles.date}>{data?.timestamp}</div>
            <div className="spacer" />
            <Stack justify="between">
              {debugSQLData && debugSQLData["debug_sql"] && (
                <>
                  {!showDebugData && (
                    <Card className={styles.help_me_debug_sql_button} onClick={toggleDebugSQL}>
                      <CardContent className="al-pt-4">
                        <div className={styles.help_me_debug_sql_text}>Help Me Debug!</div>
                      </CardContent>
                    </Card>
                  )}
                  {showDebugData && (
                    <Card className={styles.hide_debug_workflow_button} onClick={toggleDebugSQL}>
                      <CardContent>
                        <div className={styles.hide_debug_sql_text}>Hide Debug Agent</div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {isDebugSQLLoading && <ComponentLoader label="Loading debug SQL..." />}
            </Stack>
          </Stack>
          {debugSQLData && debugSQLData["debug_sql"] && showDebugData && (
            <Card className={styles.debug}>
              <CardContent>
                <Stack direction="column" gap="none">
                  <div className={styles.debug_sql_box_title}>
                    CLI command to start the debug agent:{" "}
                    <Tooltip title="Information about command line Incident">
                      <DangerCircleIcon className={styles.debug_info} />
                    </Tooltip>
                  </div>
                  <Card className={styles.command}>
                    <CardContent>
                      <Stack direction="column" gap="none">
                        <div className={styles.command_text}>
                          altimate incident debug -i{" "}
                          <span style={{ color: "#247EFE" }}>{incidentId}</span>
                        </div>
                        <div className={styles.copy_button}>
                          <CopyIconButtonFab value={`altimate incident debug -i ${incidentId}`} />
                        </div>
                      </Stack>
                    </CardContent>
                  </Card>
                  <div className={styles.debug_sql_box_title}>SQL query to view bad data:</div>
                  <ROCodeEditor value={debugSQLData["debug_sql"]} height="207px" />
                </Stack>
              </CardContent>
            </Card>
          )}
          <Card className={styles.warning}>
            <CardContent>
              <Stack align="center">
                <img src={warning} alt="Warning" className="me-4" />
                <div className={styles.warning_normal_text}>
                  {/*
                  The code below will split the term message
                  based on @ and % and then a class will be applied based on that
                  text inside @@ will be light orange
                  text inside %% will be bold orange
                  */}
                  {data?.term_message.split("\n").map((line, index) => (
                    <div key={index}>
                      {line.split(/(@[^@]*@|%[^%]*%)/g).map((text, index) => {
                        const match = text.match(/@([^@]*)@|%([^%]*)%/);
                        if (match) {
                          return (
                            <span
                              key={index}
                              className={classnames({
                                [styles.warning_orange_text]: match[1],
                                [styles.warning_bold_orange_text]: match[2],
                              })}
                            >
                              {match[1] || match[2]}
                            </span>
                          );
                        } else {
                          return text;
                        }
                      })}
                      <br />
                    </div>
                  ))}
                </div>
              </Stack>
            </CardContent>
          </Card>
          <Card className={styles.info}>
            <CardContent>
              <Stack direction="column" gap="none">
                <Stack gap="none" className={data?.filters ? "mb-2" : ""}>
                  <div className={styles.info_title}>Scope :</div>
                  <div className={styles.info_detail}>
                    {data?.scope.map(getScopeString).join(", ")}
                  </div>
                </Stack>
                {data?.filters && (
                  <Stack gap="none">
                    <div className={styles.info_title}>Filter&nbsp;&nbsp;:</div>
                    <div className={styles.info_detail}>{data?.filters}</div>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
          <Card className={styles.policy}>
            <CardContent className={styles.policy_box}>
              <div className={styles.policy_title}>Policy</div>
              <div className={styles.policy_header}>
                <div className={styles.policy_heading}>Policy Name:</div>
                <div className={"al-break-words"}>{data?.policy_name}</div>
              </div>
              <div className={styles.policy_details}>
                <div className={styles.policy_heading}>Policy Term:</div>
                <Stack direction="column" gap="none">
                  <div className={styles.policy_term}>
                    <CueCodeEditor value={data?.policy_term} height="20vh" />
                  </div>
                  <Stack gap="sm" className="mb-2">
                    {[
                      {
                        value: "24h",
                        predicate:
                          data?.status === "snoozed" && isSnoozedFor1Day(data.snoozed_till),
                        label: "Snooze (24 hours)",
                      },
                      {
                        value: "1w",
                        predicate:
                          data?.status === "snoozed" && !isSnoozedFor1Day(data.snoozed_till),
                        label: "Snooze (1 week)",
                      },
                    ].map(item => (
                      <Button
                        key={item.value}
                        color={item.predicate ? "success" : "primary"}
                        disabled={data?.status !== "active" && data?.status !== "snoozed"}
                        onClick={e => {
                          e.stopPropagation();
                          mutate({ id: incidentId, snoozed_till: item.value });
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              </div>
            </CardContent>
          </Card>
          <Card className={styles.actual}>
            <CardContent className={styles.actual_body}>
              <div className={styles.actual_title}>Actual Value&nbsp;&nbsp;:</div>
              <div className={styles.actual_detail}>{data?.actual}</div>
            </CardContent>
          </Card>
          <Card className={styles.last_incident}>
            <CardContent>
              <div className={styles.last_incident_title}>Past Incidents for the Policy Term:</div>
              <div className={styles.history_table_header}>
                <Stack justify="between" align="center" gap="none">
                  <span>Incident ID</span>
                </Stack>
                <Stack justify="between" align="center" gap="none">
                  <span>Timestamp</span>
                </Stack>
                <div>Actual Value</div>
              </div>
              <div>
                {(sortedIncidents || []).map(item => (
                  <div
                    key={item.id}
                    className={classnames(styles.history_table_row, styles.cursor_pointer)}
                    role="button"
                    tabIndex={0}
                    onClick={e => {
                      e.preventDefault();
                      onIncidentClick(item.id);
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onIncidentClick(item.id);
                      }
                    }}
                  >
                    <div className={recentIncidentIds[item.id] ? "text-muted" : "text-primary"}>
                      {item.id}
                    </div>
                    <div>{formatDateV2(item.violated_on)}</div>
                    <div>{item.actual}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { IncidentModal };
